/**
 * My Capabilities Page
 *
 * Shows the plan tier BeHeard applies, Central's subscription, and every
 * capability Central grants this workspace for BeHeard — all from GET
 * /billing/status (`@beheard/billing-contract`). When the status cannot be
 * read it says so; it never shows a default plan or an empty grant.
 * Route: /my-capabilities
 */

import { useNavigate } from 'react-router-dom';
import { BILLING_STATUS_UNAVAILABLE_MESSAGE, useGetBillingStatusQuery } from '../store/api/billingApi';
import { planNameOf } from '../utils/billingStatusView';
import { BILLING_CAPACITY_TIER_LABEL, BILLING_CAPACITY_TIER_NOTE } from '@beheard/billing-contract';

export function MyCapabilitiesPage() {
  const navigate = useNavigate();
  const { data: billing, isLoading: billingLoading, isError, refetch } = useGetBillingStatusQuery();

  if (billingLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const planName = planNameOf(billing);
  if (isError || !billing || !planName) {
    return (
      <div className="card" data-testid="capabilities-unavailable">
        <div className="card-body space-y-3 text-center">
          <h1 className="text-lg font-semibold text-gray-900">{BILLING_STATUS_UNAVAILABLE_MESSAGE}</h1>
          <button onClick={() => refetch()} className="btn-secondary">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const subscription = billing.subscription.available ? billing.subscription.subscription : null;

  // Group granted capabilities ('domain:action') by domain
  const capabilityCategories: Record<string, string[]> = {};
  billing.capabilities.granted.forEach((cap) => {
    const category = cap.split(':')[0] || 'other';
    if (!capabilityCategories[category]) {
      capabilityCategories[category] = [];
    }
    capabilityCategories[category].push(cap);
  });

  const categoryLabels: Record<string, string> = {
    practice: 'Practice',
    coach: 'Coaching',
    org: 'Organization',
    recruiter: 'Recruiter',
    public: 'Portfolio',
    portfolio: 'Portfolio',
  };

  const categoryIcons: Record<string, string> = {
    practice: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    coach: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    org: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    recruiter: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    public: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    portfolio: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  };

  const formatCapabilityName = (key: string): string => {
    const action = key.split(':')[1] ?? key;
    return action.charAt(0).toUpperCase() + action.slice(1);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Capabilities</h1>
        <p className="mt-1 text-gray-500">
          View your current plan and every feature you have access to.
        </p>
      </div>

      {/* Current Plan */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div>
              {/* BeHeard's noncommercial capacity tier — not the billing plan. */}
              <h2 className="text-lg font-semibold text-gray-900">{BILLING_CAPACITY_TIER_LABEL}</h2>
              <p className="text-xs text-gray-500" data-testid="capabilities-capacity-tier-note">{BILLING_CAPACITY_TIER_NOTE}</p>
              <div className="mt-2">
                <span className="text-2xl font-bold text-primary-600" data-testid="capabilities-plan-name">
                  {planName}
                </span>
                {subscription?.status === 'trialing' && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    Trial
                  </span>
                )}
                {subscription?.cancelAtPeriodEnd && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    Canceling
                  </span>
                )}
              </div>
              {subscription?.currentPeriodEnd && (
                <p className="mt-1 text-sm text-gray-500">
                  {subscription.cancelAtPeriodEnd ? 'Access until' : 'Renews'}{' '}
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              )}
              {!billing.subscription.available && (
                <p className="mt-1 text-sm text-amber-700">Subscription details are temporarily unavailable from LevelUp.</p>
              )}
            </div>
            <button
              onClick={() => navigate('/settings/billing')}
              className="btn btn-primary"
            >
              Manage Plan
            </button>
          </div>

        </div>
      </div>

      {/* All Capabilities by Category */}
      <div className="card">
        <div className="card-body">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">All Your Capabilities</h2>

          {billing.capabilities.granted.length === 0 ? (
            <p className="text-gray-500">
              {billing.capabilities.source === 'jwt_fallback'
                ? "Your workspace's capabilities couldn't be loaded from LevelUp just now."
                : "You don't have any capabilities yet. Upgrade your plan to unlock features."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(capabilityCategories).map(([category, caps]) => (
                <div key={category} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={categoryIcons[category] || 'M5 13l4 4L19 7'}
                      />
                    </svg>
                    <h3 className="font-medium text-gray-900">
                      {categoryLabels[category] || category.charAt(0).toUpperCase() + category.slice(1)}
                    </h3>
                  </div>
                  <ul className="space-y-1">
                    {caps.map((cap) => (
                      <li key={cap} className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formatCapabilityName(cap)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manage Subscription */}
      <div className="text-center">
        <button
          onClick={() => navigate('/settings/billing')}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Manage Subscription in LevelUp
        </button>
      </div>
    </div>
  );
}
